import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import {
  checkUserBrokers,
  getUserBrokers,
  type AdminBrokerRow,
} from "../../api/admin";
import Button from "../../components/Button";
import Card from "../../components/Card";
import Chip from "../../components/Chip";
import { apiErrorMessage } from "../../lib/api";
import { formatIst, formatRelative } from "../../lib/format";

const CHANNEL_LABEL: Record<string, string> = {
  rest_auth: "Login",
  order_api: "Orders",
  market_feed: "Tick feed",
  instruments_master: "Instruments",
};

/** The format helpers expect a naive-UTC or `Z` timestamp; the API sends
 *  an explicit `+00:00` offset here. */
function toZ(iso: string): string {
  return iso.replace(/\+00:00$/, "Z");
}

/** A user's broker connections and whether each one worked when last checked.
 *  "Check now" runs the same check the user's own Brokers page runs. */
export default function UserBrokersCard({
  userId,
  ownerLabel,
}: {
  userId: string;
  ownerLabel: string;
}) {
  const qc = useQueryClient();
  const key = ["admin-user-brokers", userId];
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => getUserBrokers(userId),
    // Cheap: reads the backend's cache, contacts no broker. Picks up the
    // background sweep's results while a deployment is running.
    refetchInterval: 30_000,
  });
  const check = useMutation({
    mutationFn: () => checkUserBrokers(userId),
    onSuccess: (rows) => qc.setQueryData(key, rows),
  });

  const rows = data ?? [];

  return (
    <Card
      title="Brokers"
      subtitle={`${ownerLabel}'s broker connections and their status when last checked. Not affected by the All / Paper / Live switch.`}
      actions={
        <Button
          size="sm"
          variant="secondary"
          iconLeft={<RefreshCw size={12} className={check.isPending ? "animate-spin" : ""} />}
          disabled={check.isPending || rows.every((r) => !r.is_active)}
          onClick={() => check.mutate()}
          title={`Run the connectivity check now, using ${ownerLabel}'s own credentials (read-only)`}
        >
          {check.isPending ? "Checking…" : "Check now"}
        </Button>
      }
    >
      {check.isError && (
        <div className="text-xs text-down-700 dark:text-down-400 mb-2">
          {apiErrorMessage(check.error)}
        </div>
      )}
      {isLoading ? (
        <div className="text-sm text-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-muted">No broker connections added.</div>
      ) : (
        <div className="divide-y divide-border">
          {rows.map((r) => (
            <BrokerRow key={r.id} row={r} />
          ))}
        </div>
      )}
    </Card>
  );
}

/** OK = every channel passed. Degraded = login works but something else
 *  failed (e.g. orders blocked by the IP allowlist, or today's instruments
 *  list not downloaded) — the connection is usable for some things. Failing =
 *  login itself failed, so nothing on this connection works. */
function healthOf(row: AdminBrokerRow): "ok" | "degraded" | "failing" | null {
  if (row.ok === null) return null;
  if (row.ok) return "ok";
  const auth = row.channels.filter((c) => c.channel === "rest_auth");
  if (auth.length > 0 && auth.every((c) => c.ok)) return "degraded";
  return "failing";
}

function BrokerRow({ row }: { row: AdminBrokerRow }) {
  const health = healthOf(row);
  const status = !row.is_active ? (
    <Chip>inactive</Chip>
  ) : health === null ? (
    <Chip>not checked</Chip>
  ) : health === "ok" ? (
    <Chip tone="up">OK</Chip>
  ) : health === "degraded" ? (
    <Chip tone="warn">degraded</Chip>
  ) : (
    <Chip tone="down">failing</Chip>
  );
  return (
    <div className="py-2.5 first:pt-0 last:pb-0 space-y-1">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium text-fg capitalize">{row.broker}</span>
        <span className="text-muted">·</span>
        <span className="text-fg-subtle">{row.label}</span>
        {status}
        <span className="ml-auto text-xs text-muted">
          {row.checked_at ? (
            <span title={formatIst(toZ(row.checked_at), { withSeconds: true })}>
              checked {formatRelative(toZ(row.checked_at))} ·{" "}
              {formatIst(toZ(row.checked_at))}
            </span>
          ) : row.is_active ? (
            "not checked since the server started"
          ) : null}
        </span>
      </div>
      {row.channels.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
          {row.channels.map((c) => (
            <span
              key={c.channel}
              title={c.message}
              className={
                c.ok ? "text-up-700 dark:text-up-400" : "text-down-700 dark:text-down-400"
              }
            >
              {c.ok ? "✓" : "✗"} {CHANNEL_LABEL[c.channel] ?? c.channel}
              {!c.ok && <span className="text-fg-subtle"> — {c.message}</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
